// Infrastructure Service - Firebase Auth Service
// Complete implementation for authentication services

import { IAuthService } from '@modules/user-management/users/domain/services/IAuthService';
import { User, UserStatus, UserRole } from '@modules/user-management/users/domain/entities/User';
import { auth, db, functions } from '@/config/firebase';
import { httpsCallable } from 'firebase/functions';
import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signInWithPopup,
  GoogleAuthProvider,
  signOut, 
  onAuthStateChanged as firebaseOnAuthStateChanged,
  User as FirebaseUser,
  fetchSignInMethodsForEmail,
  linkWithCredential,
  EmailAuthProvider
} from 'firebase/auth';
import { doc, getDoc, setDoc, onSnapshot, Timestamp } from 'firebase/firestore';

export class FirebaseAuthService implements IAuthService {
  // uids deleted in this session: never re-subscribe to / recreate their users/{uid} doc,
  // even if the local sign-out after deletion failed (ID tokens stay valid for ~1h).
  private readonly deletedUids = new Set<string>();

  async signIn(credentials: { email: string; password: string }): Promise<User> {
    return this.login(credentials.email, credentials.password);
  }

  async signOut(): Promise<void> {
    return this.logout();
  }

  async resetPassword(email: string): Promise<void> {
    // Stub - would use Firebase sendPasswordResetEmail
    console.log('Reset password for:', email);
  }

  async updatePassword(currentPassword: string, newPassword: string): Promise<void> {
    // Stub - would use Firebase reauthenticateWithCredential + updatePassword
    console.log('Update password:', currentPassword, newPassword);
  }

  async login(email: string, password: string): Promise<User> {
    try {
      const credential = await signInWithEmailAndPassword(auth, email, password);
      const user = await this.getFullUserData(credential.user);
      
      if (!user) {
        throw new Error('Dados do usuário não encontrados');
      }

      return user;
    } catch (error: any) {
      // Check if the error is related to authentication method
      if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
        try {
          // Check what sign-in methods are available for this email
          const signInMethods = await fetchSignInMethodsForEmail(auth, email);
          
          if (signInMethods.length > 0) {
            if (signInMethods.includes('google.com')) {
              throw new Error('Esta conta foi criada com Google. Use "Continuar com Google" para fazer login.');
            }
            if (signInMethods.includes('password')) {
              throw new Error('Email ou senha incorretos.');
            }
          }
        } catch (methodError) {
          // If we can't check methods, fall back to original error
        }
      }
      
      throw new Error(this.getErrorMessage(error.code));
    }
  }

  async signInWithGoogle(): Promise<User> {
    try {
      const provider = new GoogleAuthProvider();
      provider.addScope('email');
      provider.addScope('profile');
      
      const credential = await signInWithPopup(auth, provider);
      
      const user = await this.getFullUserData(credential.user);
      
      if (!user) {
        throw new Error('Dados do usuário não encontrados');
      }

      return user;
    } catch (error: any) {
      throw new Error(this.getErrorMessage(error.code || 'unknown'));
    }
  }

  async register(email: string, password: string, displayName: string): Promise<User> {
    try {
      const credential = await createUserWithEmailAndPassword(auth, email, password);
      
      // Create user document in Firestore
      const userData: Omit<User, 'id'> = {
        email: email.toLowerCase(),
        displayName,
        role: UserRole.Member,  // Membro da igreja
        status: UserStatus.Pending,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      // Remove undefined fields before saving to Firestore
      const firestoreData: any = {
        email: userData.email,
        displayName: userData.displayName,
        role: userData.role,
        status: userData.status,
        createdAt: Timestamp.fromDate(userData.createdAt),
        updatedAt: Timestamp.fromDate(userData.updatedAt)
      };

      await setDoc(doc(db, 'users', credential.user.uid), firestoreData);

      return {
        id: credential.user.uid,
        ...userData
      };
    } catch (error: any) {
      throw new Error(this.getErrorMessage(error.code));
    }
  }

  async logout(): Promise<void> {
    try {
      await signOut(auth);
    } catch (error: any) {
      throw new Error('Erro ao sair da conta');
    }
  }

  async getCurrentUser(): Promise<User | null> {
    const firebaseUser = auth.currentUser;
    if (!firebaseUser) {
      return null;
    }

    return await this.getFullUserData(firebaseUser);
  }

  onAuthStateChanged(callback: (user: User | null) => void): () => void {
    let unsubscribeUserDoc: (() => void) | undefined;

    const unsubscribeAuth = firebaseOnAuthStateChanged(auth, (firebaseUser) => {
      unsubscribeUserDoc?.();
      unsubscribeUserDoc = undefined;

      if (!firebaseUser || this.deletedUids.has(firebaseUser.uid)) {
        callback(null);
        return;
      }

      unsubscribeUserDoc = onSnapshot(
        doc(db, 'users', firebaseUser.uid),
        async (userDoc) => {
          try {
            if (!userDoc.exists()) {
              const user = await this.getFullUserData(firebaseUser);
              callback(user);
              return;
            }
            callback(this.mapFirestoreUser(firebaseUser.uid, userDoc.data()));
          } catch (error: any) {
            console.warn('Error fetching user data, but user is authenticated:', error);
            callback(this.buildFallbackUser(firebaseUser));
          }
        },
        async (error) => {
          console.warn('Error subscribing to user profile:', error);
          try {
            const user = await this.getFullUserData(firebaseUser);
            callback(user ?? this.buildFallbackUser(firebaseUser));
          } catch {
            callback(this.buildFallbackUser(firebaseUser));
          }
        }
      );
    });

    return () => {
      unsubscribeUserDoc?.();
      unsubscribeAuth();
    };
  }

  async linkEmailPassword(password: string): Promise<void> {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('Nenhum usuário logado');
      }

      // Check if user already has password authentication
      const signInMethods = await fetchSignInMethodsForEmail(auth, currentUser.email!);
      if (signInMethods.includes('password')) {
        throw new Error('Este usuário já possui uma senha definida');
      }

      // Create email credential and link it
      const credential = EmailAuthProvider.credential(currentUser.email!, password);
      await linkWithCredential(currentUser, credential);
    } catch (error: any) {
      throw new Error(this.getErrorMessage(error.code));
    }
  }

  /**
   * Calls the `deleteOwnAccount` callable (server uses only the caller's uid) and signs out.
   * Callers must tear down the users/{uid} listener BEFORE calling this, otherwise the
   * listener would recreate the deleted profile document (see AuthContext.deleteAccount).
   * Errors keep the server's PT-BR message and the `functions/<code>` code.
   */
  async deleteAccount(): Promise<void> {
    const uid = auth.currentUser?.uid;
    try {
      await httpsCallable(functions, 'deleteOwnAccount')({});
    } catch (error: any) {
      const code: string = error?.code || 'functions/internal';
      const serverMessage: string | undefined = error?.message;
      const message = serverMessage && !['internal', 'INTERNAL', 'unavailable'].includes(serverMessage)
        ? serverMessage
        : 'Não foi possível excluir sua conta agora. Verifique sua conexão e tente novamente.';
      // details.reason: 'last-admin' | 'recent-login-required' (set by the callable)
      throw Object.assign(new Error(message), { code, details: error?.details });
    }

    if (uid) this.deletedUids.add(uid);

    try {
      await signOut(auth);
    } catch (error) {
      // The account no longer exists on the server; a local sign-out failure is harmless.
      console.warn('Sign out after account deletion failed:', error);
    }
  }

  async getSignInMethods(email: string): Promise<string[]> {
    try {
      return await fetchSignInMethodsForEmail(auth, email);
    } catch (error: any) {
      throw new Error('Erro ao verificar métodos de login');
    }
  }

  private async getFullUserData(firebaseUser: FirebaseUser): Promise<User | null> {
    if (this.deletedUids.has(firebaseUser.uid)) {
      return null;
    }
    try {
      const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
      
      if (!userDoc.exists()) {
        // Create default user document if it doesn't exist
        const userData: Omit<User, 'id'> = {
          email: firebaseUser.email || '',
          displayName: firebaseUser.displayName || firebaseUser.email || '',
          role: UserRole.Member,  // Membro da igreja
          status: UserStatus.Pending,
          createdAt: new Date(),
          updatedAt: new Date(),
          photoURL: firebaseUser.photoURL || undefined,
          phoneNumber: firebaseUser.phoneNumber || undefined
        };

        // Remove undefined fields before saving to Firestore
        const firestoreData: any = {
          email: userData.email,
          displayName: userData.displayName,
          role: userData.role,
          status: userData.status,
          createdAt: Timestamp.fromDate(userData.createdAt),
          updatedAt: Timestamp.fromDate(userData.updatedAt)
        };

        if (userData.photoURL) {
          firestoreData.photoURL = userData.photoURL;
        }

        if (userData.phoneNumber) {
          firestoreData.phoneNumber = userData.phoneNumber;
        }

        if (userData.biography) {
          firestoreData.biography = userData.biography;
        }

        try {
          await setDoc(doc(db, 'users', firebaseUser.uid), firestoreData);
        } catch (createError: any) {
          if (createError?.code === 'permission-denied') {
            // A deletedAccounts tombstone exists (account deleted, possibly from another
            // tab/device): stop treating this uid as a live account — no retry, no noise.
            this.deletedUids.add(firebaseUser.uid);
            return null;
          }
          throw createError;
        }

        return {
          id: firebaseUser.uid,
          ...userData
        };
      }

      const data = userDoc.data();
      return this.mapFirestoreUser(firebaseUser.uid, data);
    } catch (error) {
      // Silently fail and return null
      return null;
    }
  }

  private mapFirestoreUser(uid: string, data: any): User {
    return {
      id: uid,
      email: data.email,
      displayName: data.displayName,
      role: data.role as UserRole,
      status: data.status as UserStatus,
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date(),
      photoURL: data.photoURL,
      phoneNumber: data.phoneNumber,
      biography: data.biography
    };
  }

  private buildFallbackUser(firebaseUser: FirebaseUser): User {
    return {
      id: firebaseUser.uid,
      email: firebaseUser.email || '',
      displayName: firebaseUser.displayName || 'Usuário',
      role: UserRole.Member,
      status: UserStatus.Approved,
      createdAt: new Date(),
      updatedAt: new Date()
    };
  }

  private getErrorMessage(errorCode: string): string {
    switch (errorCode) {
      case 'auth/user-not-found':
      case 'auth/wrong-password':
      case 'auth/invalid-credential':
        return 'Email ou senha incorretos';
      case 'auth/email-already-in-use':
        return 'Este email ja esta em uso';
      case 'auth/weak-password':
        return 'A senha deve ter pelo menos 6 caracteres';
      case 'auth/invalid-email':
        return 'Email invalido';
      case 'auth/user-disabled':
        return 'Esta conta foi desativada. Entre em contato com o administrador';
      case 'auth/too-many-requests':
        return 'Muitas tentativas. Tente novamente mais tarde';
      case 'auth/popup-closed-by-user':
      case 'auth/cancelled-popup-request':
        return 'Login cancelado';
      case 'auth/popup-blocked':
        return 'Popup bloqueado pelo navegador. Permita popups para este site';
      case 'auth/network-request-failed':
        return 'Erro de conexao. Verifique sua internet';
      case 'auth/unauthorized-domain':
        return 'Este dominio nao esta autorizado para login';
      case 'auth/operation-not-allowed':
        return 'Este metodo de login nao esta habilitado. Entre em contato com o administrador';
      default:
        console.error('Auth error:', errorCode);
        return 'Ocorreu um erro ao autenticar. Tente novamente';
    }
  }
}
